import { useState } from 'react'
import type { AvisoResumen } from '../../hooks/useAvisos'
import {
  useGestionarAviso,
  useGuardarNotaAviso,
  useDarDeBajaAviso,
} from '../../hooks/useAvisos'
import { InsigniaEstado } from './InsigniaEstado'
import { MarcaSemanas } from './MarcaSemanas'
import { formatearFechaCorta } from './formato'
import { PerfilCliente } from '../../componentes/PerfilCliente'
import { IconoCorrecto } from '../../componentes/iconos'

/**
 * Fila de aviso ACCIONABLE (B.11), opción C: al pulsarla se expande un panel
 * con la nota de texto libre (histórico de lo hablado) y las acciones —marcar
 * gestión realizada, abrir ficha, dar de baja—. La tabla queda compacta cerrada.
 */
export function FilaAvisoExpandible({ aviso }: { aviso: AvisoResumen }) {
  const [abierta, setAbierta] = useState(false)
  const [nota, setNota] = useState(aviso.notas ?? '')
  const [verPerfil, setVerPerfil] = useState(false)

  const gestionar = useGestionarAviso()
  const guardarNota = useGuardarNotaAviso()
  const darBaja = useDarDeBajaAviso()

  const ocupado = gestionar.isPending || guardarNota.isPending || darBaja.isPending

  return (
    <li className="overflow-hidden rounded-xl border border-borde bg-superficie">
      {/* Cabecera pulsable */}
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        aria-expanded={abierta}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-elevado"
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-tinta">{aviso.nombre}</p>
          <p className="flex flex-wrap items-center gap-x-3 text-xs text-tinta-apagada">
            <span>{aviso.telefono || 'Sin teléfono'}</span>
            <span>Último albarán: {formatearFechaCorta(aviso.fecha_ultimo_albaran ?? '') ?? 'sin albarán'}</span>
            {aviso.notas ? <span className="italic text-tinta-2">· con nota</span> : null}
          </p>
        </div>
        <MarcaSemanas semanas={aviso.semanas_sin_pedir} />
        <InsigniaEstado estado={aviso.estado} />
        <span aria-hidden className={['text-tinta-apagada transition-transform', abierta ? 'rotate-180' : ''].join(' ')}>
          ▾
        </span>
      </button>

      {/* Panel de gestión */}
      {abierta ? (
        <div className="flex flex-col gap-3 border-t border-borde px-4 py-3 sm:flex-row sm:items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-wide text-tinta-apagada">
              Nota de lo hablado
            </label>
            <textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Motivo, circunstancia del cliente, qué se habló…"
              className="min-h-16 w-full resize-y rounded-lg border border-borde-fuerte bg-lienzo px-3 py-2 text-sm text-tinta placeholder:text-tinta-apagada"
            />
            <button
              type="button"
              disabled={ocupado || nota === (aviso.notas ?? '')}
              onClick={() => guardarNota.mutate({ id: aviso.id, nota })}
              className="w-fit text-xs font-medium text-acento-tinta underline underline-offset-2 hover:text-acento-fuerte disabled:opacity-40"
            >
              {guardarNota.isPending ? 'Guardando…' : 'Guardar nota'}
            </button>
          </div>

          <div className="flex shrink-0 flex-col gap-2">
            <button
              type="button"
              disabled={ocupado}
              onClick={() => gestionar.mutate({ id: aviso.id, nota: nota || undefined })}
              className="flex items-center justify-center gap-1.5 rounded-lg border border-positivo-anillo bg-positivo-fondo px-3 py-1.5 text-xs font-semibold text-positivo-tinta transition hover:brightness-110 disabled:opacity-50"
            >
              <IconoCorrecto className="size-4" />
              Marcar gestionado
            </button>
            <button
              type="button"
              onClick={() => setVerPerfil(true)}
              className="rounded-lg border border-borde-fuerte px-3 py-1.5 text-xs font-semibold text-tinta-2 transition hover:bg-elevado"
            >
              Ver perfil
            </button>
            <button
              type="button"
              disabled={ocupado}
              onClick={() => darBaja.mutate({ id: aviso.id, hasta: undefined })}
              className="rounded-lg border border-critico-anillo bg-critico-fondo px-3 py-1.5 text-xs font-semibold text-critico-tinta transition hover:brightness-110 disabled:opacity-50"
            >
              {darBaja.isPending ? 'Dando de baja…' : 'Dar de baja'}
            </button>
          </div>
        </div>
      ) : null}

      {gestionar.isError || guardarNota.isError || darBaja.isError ? (
        <p role="alert" className="border-t border-borde px-4 py-2 text-xs text-critico-tinta">
          No se pudo completar la acción. Inténtalo de nuevo.
        </p>
      ) : null}

      <PerfilCliente contactId={verPerfil ? aviso.id : null} onCerrar={() => setVerPerfil(false)} />
    </li>
  )
}
